import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import DashboardLayout from '../../../components/dashboard/DashboardLayout';
import { Card, CardBody } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';
import { theme } from '../../../theme';
import { useAuth } from '../../../contexts/AuthContext';
import {
  Complain,
  ComplainStatus,
  COMPANY_COMPLAIN_STATUSES,
  complainDateToJs,
  effectiveCompletedAtForDetail,
  effectiveResolvedAtForDetail,
  formatComplainDisplayDate,
  formatComplainStatusLabel,
  fetchComplainById,
  updateComplainStatus,
} from '../service/complainService';

const PageHeader = styled.div`
  margin-bottom: ${theme.spacing[6]};
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: ${theme.spacing[4]};
`;

const PageTitle = styled.h1`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  margin: 0 0 ${theme.spacing[2]} 0;
`;

const Meta = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
  font-family: monospace;
`;

const Field = styled.div`
  margin-bottom: ${theme.spacing[4]};
`;

const Label = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.light.text.secondary};
  margin-bottom: ${theme.spacing[1]};
`;

const Value = styled.div`
  color: ${theme.colors.light.text.primary};
  white-space: pre-wrap;
`;

const StatusBadge = styled.span<{ variant?: 'completed' | 'resolved' | 'rejected' }>`
  display: inline-block;
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  background-color: ${({ variant }) => {
    if (variant === 'rejected') return `${theme.colors.accent.danger}18`;
    if (variant === 'completed' || variant === 'resolved') return `${theme.colors.accent.success}22`;
    return `${theme.colors.secondary[400]}25`;
  }};
  color: ${({ variant }) => {
    if (variant === 'rejected') return theme.colors.accent.danger;
    if (variant === 'completed' || variant === 'resolved') return theme.colors.accent.success;
    return theme.colors.secondary[700];
  }};
`;

const StatusStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

function formatStatus(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const CompanyComplainDetailPage: React.FC = () => {
  const { companyId, complainId } = useParams<{ companyId: string; complainId: string }>();
  const { companyData } = useAuth();
  const navigate = useNavigate();
  const [row, setRow] = useState<Complain | null>(null);
  const [status, setStatus] = useState<ComplainStatus>('open');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const allowed = companyData?.id && companyId === companyData.id;

  useEffect(() => {
    if (!allowed || !companyId || !complainId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const c = await fetchComplainById(companyId, complainId);
        if (!cancelled) {
          setRow(c);
          if (c) setStatus(c.status);
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) setRow(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [allowed, companyId, complainId]);

  const save = async () => {
    if (!row || !companyId) return;
    try {
      setSaving(true);
      await updateComplainStatus(companyId, row.bankId, row.branchId, row.id, status);
      toast.success('Status updated');
      setRow({ ...row, status });
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  if (!companyId || !complainId) {
    return (
      <DashboardLayout>
        <Card>
          <CardBody>Invalid route.</CardBody>
        </Card>
      </DashboardLayout>
    );
  }

  if (!allowed) {
    return (
      <DashboardLayout>
        <Card>
          <CardBody>You do not have access to this complain.</CardBody>
        </Card>
      </DashboardLayout>
    );
  }

  if (loading) {
    return (
      <DashboardLayout>
        <Card>
          <CardBody>Loading…</CardBody>
        </Card>
      </DashboardLayout>
    );
  }

  if (!row) {
    return (
      <DashboardLayout>
        <Card>
          <CardBody>Complain not found.</CardBody>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <PageHeader>
        <div>
          <PageTitle>Complain</PageTitle>
          <Meta>
            ID: {row.id}
          </Meta>
        </div>
        <Button variant="secondary" type="button" onClick={() => navigate(`/companies/${companyId}/complains`)}>
          Back to list
        </Button>
      </PageHeader>
      <Card>
        <CardBody>
          <Field>
            <Label>Type</Label>
            <Value>{row.type}</Value>
          </Field>
          <Field>
            <Label>Description</Label>
            <Value>{row.description}</Value>
          </Field>
          <Field>
            <Label>Branch</Label>
            <Value>{row.address || row.bankName || '—'}</Value>
          </Field>
          <Field>
            <Label>Status</Label>
            {row.status === 'resolved' || row.status === 'completed' ? (
              <StatusBadge variant={row.status === 'resolved' ? 'resolved' : 'completed'}>
                {formatComplainStatusLabel(row.status)}
              </StatusBadge>
            ) : row.status === 'rejected' ? (
              <StatusStack>
                <StatusBadge variant="rejected">{formatComplainStatusLabel('rejected')}</StatusBadge>
                <Select
                  aria-label="Update from rejected"
                  value=""
                  size="sm"
                  dark
                  onChange={async (e) => {
                    const v = e.target.value as ComplainStatus | '';
                    if (v !== 'completed' || !row || !companyId) return;
                    try {
                      setSaving(true);
                      await updateComplainStatus(companyId, row.bankId, row.branchId, row.id, 'completed');
                      toast.success('Status updated');
                      setRow({ ...row, status: 'completed', rejectionReason: undefined });
                      setStatus('completed');
                    } catch (err: any) {
                      console.error(err);
                      toast.error(err?.message || 'Failed to update');
                    } finally {
                      setSaving(false);
                    }
                  }}
                  fullWidth
                  disabled={saving}
                >
                  <option value="">Change status…</option>
                  <option value="completed">{formatStatus('completed')}</option>
                </Select>
              </StatusStack>
            ) : (
              <Select
                value={status}
                size="sm"
                dark
                onChange={(e) => setStatus(e.target.value as ComplainStatus)}
                fullWidth
              >
                {COMPANY_COMPLAIN_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {formatStatus(s)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          {row.status === 'rejected' && row.rejectionReason ? (
            <Field>
              <Label>Rejection reason</Label>
              <Value>{row.rejectionReason}</Value>
            </Field>
          ) : null}
          <Field>
            <Label>Created</Label>
            <Value>{formatComplainDisplayDate(row.createdAt)}</Value>
          </Field>
          {complainDateToJs(effectiveCompletedAtForDetail(row)) ? (
            <Field>
              <Label>Completed on</Label>
              <Value>{formatComplainDisplayDate(effectiveCompletedAtForDetail(row))}</Value>
            </Field>
          ) : null}
          {complainDateToJs(effectiveResolvedAtForDetail(row)) ? (
            <Field>
              <Label>Resolved on</Label>
              <Value>{formatComplainDisplayDate(effectiveResolvedAtForDetail(row))}</Value>
            </Field>
          ) : null}
          <Button
            type="button"
            onClick={save}
            disabled={
              saving ||
              row.status === 'rejected' ||
              row.status === 'resolved' ||
              row.status === 'completed'
            }
          >
            {saving ? 'Saving…' : 'Save status'}
          </Button>
        </CardBody>
      </Card>
    </DashboardLayout>
  );
};

export default CompanyComplainDetailPage;
